import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import Recaptcha from "react-recaptcha";
import classNames from "classnames";

import Form, { InputText, Select, Validation } from "../../../../components/Form";

import { ICh_ChoirApplication } from "../../../../../common/types/choir/choirApplication";
import { CHOIR_VOICES, TChoirVoice } from "../../../../../common/types/choir/meta/general";

import { post } from "../../../../actions/general";
import { ISelectEvent } from "../../../../types";

import { translateVoices } from "../General/WanterVoices/utils";

import { Container, FormContainer, Row } from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";
import SuccessModal from "./SuccessModal";
import { recaptchaSiteKey } from "../../../../../common/parameters";
import { useUser } from "../../../../store/user/selectors";
import { StyledRecaptcha } from "../../../../pages/contact/styles";

let $script = (a: string, b: string) => null;
if (process.env.WEBPACK) {
    $script = require("scriptjs");
}

const ApplicationForm = ({ choir, applicationFormRef }: IProps) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const { formatMessage, locale } = useIntl();
    const [isLoading, setLoading] = useState(false);
    const [isSuccess, setSuccess] = useState(false);
    const dispatch = useDispatch();
    const [validation, setValidation] = useState<string | null>(null);
    const [voice, setVoice] = useState<TChoirVoice>(undefined);
    const [recaptcha, setRecaptcha] = useState('');
    const recaptchaRef = useRef<Recaptcha>();
    const user = useUser();

    useEffect(() => {
        if (!user._id) {
            $script("https://www.google.com/recaptcha/api.js", "recaptcha");
        }
    }, [])

    const handleSubmit = () => {
        setValidation(null);
        setLoading(true);
        const data: Omit<ICh_ChoirApplication, '_id'> & { 'g-recaptcha-response': string } = {
            choir: choir._id,
            name: { first: firstName, last: lastName },
            email,
            phone,
            voice,
            "g-recaptcha-response": recaptcha,
        }
        dispatch(post('choirs/choir-application/create', data, ({ data }) => {
            setLoading(false);
            if (data.success) {
                setFirstName('');
                setLastName('');
                setEmail('');
                setPhone('');
                setVoice(undefined);
                setSuccess(true);
            } else {
                setValidation('unknownError');
            }
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();

            }
        }, () => {
            setLoading(false);
            setValidation('unknownError');
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }

        }));
    };

    const voiceOptions = CHOIR_VOICES.filter(cv => choir.meta.general.wantedVoices.includes(cv)).map(voice => ({
        value: voice,
        label: translateVoices([voice])[0]
    }));

    const handleChangeVoice = (event: ISelectEvent<TChoirVoice>) => {
        setVoice(event.target.value);
    }

    const disabled = !firstName || !lastName || !email || !phone || (!recaptcha && !user._id);

    const changeRecaptcha = (e: string) => {
        setRecaptcha(e);
    }

    return (
        <Container ref={applicationFormRef}>
            <FormContainer>
                <Form disabled={isLoading}>
                    <h2>
                        <FormattedMessage {...messages.title} />
                    </h2>
                    {validation && (
                        <Validation validation={validation} />
                    )}
                    <Row>
                        <InputText
                            name="lastName"
                            label={formatMessage(messages.lastName)}
                            value={lastName}
                            onChange={e => setLastName(e.currentTarget.value)}
                            required
                        />
                        <InputText
                            name="firstName"
                            label={formatMessage(messages.firstName)}
                            value={firstName}
                            onChange={e => setFirstName(e.currentTarget.value)}
                            required
                        />
                    </Row>
                    <InputText
                        email
                        label={formatMessage(messages.email)}
                        value={email}
                        onChange={e => setEmail(e.currentTarget.value)}
                        required
                    />
                    <InputText
                        name="phone"
                        label={formatMessage(messages.phone)}
                        value={phone}
                        onChange={e => setPhone(e.currentTarget.value)}
                        required
                    />
                    <Select value={voice} label={formatMessage(messages.voice)} options={voiceOptions} onChange={handleChangeVoice} name="voice" />
                    {process.env.WEBPACK && !user._id && (
                        <StyledRecaptcha className={classNames(isLoading && "RecaptchaDisabled")}>
                            <Recaptcha
                                ref={recaptchaRef}
                                sitekey={recaptchaSiteKey}
                                render="explicit"
                                elementID="subscribeRecaptcha"
                                hl={locale}
                                onloadCallback={() => null}
                                verifyCallback={changeRecaptcha}
                                expiredCallback={() => changeRecaptcha("")}
                                onChange={changeRecaptcha}
                                size={window.innerWidth < 360 ? "compact" : "normal"}
                            />
                        </StyledRecaptcha>
                    )}
                    <button className="button button--primary button--full-width" onClick={handleSubmit} disabled={disabled}>
                        {formatMessage(messages.send)}
                    </button>
                </Form>
            </FormContainer>
            {isSuccess && (
                <SuccessModal onClose={() => setSuccess(false)} />
            )}
        </Container>
    );
};

export default ApplicationForm;
